import type { DersdinleUser as User } from "@/lib/api";
import { useEffect, useState } from "react";
import AiCoachDrawer from "./AiCoachDrawer";
import {useAiCoach} from "@/hooks/useAiCoach";
import { useAuth } from "@/hooks/useAuth";
import {useSiteSettingsContext} from "@/hooks/useSiteSettingsContext";
import { getDataClient } from "@/lib/dataClient";
import {
  aiCoachProfiles,
  assistantAvatar,
  type AiCoachProfile,
  type CoachRole,
} from "@/mocks/aiCoach";

const ROLE_LABELS: Record<CoachRole, string> = {
  ogrenci: "Öğrenci",
  veli: "Veli",
  ogretmen: "Öğretmen",
  admin: "Yönetici",
  guest: "Ziyaretçi",
};

// Gerçek oturum bilgisinden asistan profili oluşturur.
// Oturum yoksa (guest) yalnızca tanıtım profili kullanılır.
function buildProfile(user: User | null, role: CoachRole): AiCoachProfile {
  if (!user) return aiCoachProfiles.guest;

  const meta = (user.user_metadata ?? {}) as Record<string, unknown>;
  const emailName = user.email ? user.email.split("@")[0] : "";

  return {
    name: String(meta.ad_soyad || emailName || "Kullanıcı"),
    role,
    roleLabel: ROLE_LABELS[role] ?? "Kullanıcı",
    grade: String(meta.sinif ?? ""),
    exam: "",
    city: String(meta.sehir ?? ""),
    childName: "",
    childGrade: "",
    subjects: [],
    goal: "",
  };
}

export default function AiCoach() {
  const { open, openCoach } = useAiCoach();
  const { user, role, loading } = useAuth();
  const { settings } = useSiteSettingsContext();
  const siteName = settings.siteName || "DersDinle";
  const [profile, setProfile] = useState<AiCoachProfile | null>(null);

  // Veli rolünde, eşleşen çocuğun adını ve sınıfını gerçek veritabanından çek.
  useEffect(() => {
    let cancelled = false;
    const base = buildProfile(user, role);
    setProfile(base);

    if (role === "veli" && user) {
      void (async () => {
        try {
          const { data } = await getDataClient()
            .from("profiles")
            .select("full_name, grade")
            .eq("parent_id", user.id)
            .order("created_at", { ascending: true })
            .limit(1);
          if (cancelled) return;
          const first = Array.isArray(data) && data.length > 0 ? data[0] : null;
          if (first) {
            setProfile((prev) =>
              prev
                ? {
                    ...prev,
                    childName: String(first.full_name || ""),
                    childGrade: String(first.grade || ""),
                  }
                : prev,
            );
          }
        } catch {
          // best-effort: çocuk bilgisi gelmezse asistan yine çalışır
        }
      })();
    }

    return () => {
      cancelled = true;
    };
  }, [user, role]);

  // Oturum çözümlenene kadar asistanı gösterme (yanlış "ziyaretçi" flaşını önler).
  if (loading || !profile) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => openCoach()}
        aria-label={`${siteName} Asistan'ı aç`}
        aria-expanded={open}
        className={`fixed bottom-24 right-4 md:right-6 z-50 flex items-center gap-2 whitespace-nowrap pl-3 pr-4 py-3 rounded-full bg-accent-500 text-background-50 font-semibold text-sm hover:bg-accent-600 transition-all ${
          open ? "opacity-0 pointer-events-none" : "opacity-100"
        }`}
      >
        <img
          src={assistantAvatar}
          alt={`${siteName} Asistan`}
          className="w-7 h-7 rounded-full object-cover ring-2 ring-background-50/70"
        />
        <span>{siteName} Asistan</span>
      </button>

      {/* Kullanıcı/rol değişince sohbeti sıfırla. */}
      <AiCoachDrawer key={user?.id ?? "guest"} profile={profile} siteName={siteName} />
    </>
  );
}
